<template>
  <Modal
    :title="modalTitle"
    no-submit
    prevent-close
    @hide-modal="emit('hide-modal')"
  >
    <div class="section-spacing-20">
      <div class="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <div>Your Progress</div>
          <div class="text-xl">
            <strong>{{ progress.toFixed() }}% Completed</strong>
          </div>
        </div>
        <div class="d-flex">
          <Spinner :message="progressMessage" class="mr-2" />
        </div>
      </div>
      <ProgressBar :progress="progress" />
      <div>
        <strong>
          Notice: If you close this window before the upload completes some
          {{ mediaTypeLabel }}s may be lost.
        </strong>
      </div>
      <div>
        If you need to continue working you can
        <a href="#" target="_blank">open another tab</a> and come back to this
        one in a few minutes.
      </div>
    </div>
    <template #delete>
      <div class="d-flex justify-content-start">
        <Button type="secondary" @click="emit('hide-modal')"
          >Cancel Upload</Button
        >
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Button from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import Spinner from "@/components/Spinner.vue";
import CONSTANTS from "@/Constants.js";

const { MEGABYTE } = CONSTANTS;

export type MediaType = "photo" | "video" | "document";

const emit = defineEmits(["hide-modal"]);

const props = defineProps<{
  mediaType: MediaType;
  fileCount: number;
  totalFileSize: number;
  progress: number;
  currentChunk?: number;
  totalChunks?: number;
}>();

const mediaTypeLabel = computed(() => {
  return props.mediaType;
});

const modalTitle = computed(() => {
  const labels: Record<MediaType, string> = {
    photo: "Uploading Photos",
    video: "Uploading Videos",
    document: "Uploading Documents"
  };
  return labels[props.mediaType];
});

const progressMessage = computed(() => {
  const fileSizeMB = (props.totalFileSize / MEGABYTE).toFixed(1);
  let message = `Uploading ${props.fileCount} ${mediaTypeLabel.value}(s) / ${fileSizeMB}MB total`;

  if (props.currentChunk && props.totalChunks) {
    message += ` (part ${props.currentChunk} of ${props.totalChunks})`;
  }

  return message;
});
</script>

<style lang="scss" scoped>
:deep(.progress-bar-container) {
  @apply h-4;
}
:deep(.progress-details) {
  @apply hidden;
}
</style>
